import { getBaseUrlByLanguage } from "@/lib/seo";
import type { SuccessLoveMap } from "@/components/PaymentSuccessScreen";

export const HOTMART_RETURN_STORAGE_KEY = "hotmart_checkout_return";

const HOTMART_RETURN_MAX_AGE_MS = 48 * 60 * 60 * 1000;

export function getPortugalReturnRedirectUrl(
  currentUrl: string,
  hasStoredContext: boolean,
): string | null {
  const url = new URL(currentUrl);
  if (hasStoredContext || url.hostname !== "pt.channeledletter.com") {
    return null;
  }

  return new URL(
    `${url.pathname}${url.search}${url.hash}`,
    getBaseUrlByLanguage("pt-PT"),
  ).toString();
}

export interface HotmartReturnContext {
  requestId: number;
  checkoutToken: string;
  querentName: string;
  email?: string;
  whatsappNumber?: string;
  deliveryOption?: "email" | "whatsapp" | "email_whatsapp";
  amountCents?: number;
  currency?: string;
  priorityDelivery?: boolean;
  priorityDeliveryFeeCents?: number;
  deliveryTargetMinutes?: number;
  loveMap?: SuccessLoveMap | null;
  createdAt: number;
}

export function saveHotmartReturnContext(
  context: HotmartReturnContext,
): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      HOTMART_RETURN_STORAGE_KEY,
      JSON.stringify(context),
    );
  } catch {
    // Storage can be unavailable in private or embedded browsers. Checkout
    // must still continue; the return page will show its generic fallback.
  }
}

export function loadHotmartReturnContext(): HotmartReturnContext | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(HOTMART_RETURN_STORAGE_KEY);
    if (!raw) return null;

    const value = JSON.parse(raw) as Partial<HotmartReturnContext>;
    const isValid =
      Number.isInteger(value.requestId) &&
      Number(value.requestId) > 0 &&
      typeof value.checkoutToken === "string" &&
      value.checkoutToken.length > 0 &&
      typeof value.querentName === "string" &&
      typeof value.createdAt === "number";
    const isExpired =
      typeof value.createdAt === "number" &&
      Date.now() - value.createdAt > HOTMART_RETURN_MAX_AGE_MS;

    if (!isValid || isExpired) {
      clearHotmartReturnContext();
      return null;
    }

    return value as HotmartReturnContext;
  } catch {
    clearHotmartReturnContext();
    return null;
  }
}

export function clearHotmartReturnContext(): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(HOTMART_RETURN_STORAGE_KEY);
  } catch {
    // Nothing else is required when browser storage is unavailable.
  }
}
